import type { NoteRefArt } from "../../services/vorgangService";

/**
 * Die Auswahl hinter dem `#` (PO 08.09.2026): Mail, Anruf, Dokument, Risiko,
 * Vertrag und Person. Reine Ableitung — die Fläche reicht durch, was am
 * Arbeitsstrang hängt, und bekommt eine flache, sortierte Liste zurück.
 *
 * Die Reihenfolge ist Absicht: Was schon am Vorgang hängt, steht zuerst; sie
 * spiegelt, wie oft man sich im Alltag worauf bezieht.
 */

export type BezugKandidat = {
    art: NoteRefArt;
    id: number | string;
    titel: string;
    /** Zweite Zeile im Menü — Absender, Datum, Nummer. */
    zusatz?: string;
    /**
     * Zeitpunkt, kurz (PO 09.09.2026). Nur für die Mail-Karte im Notiztext —
     * im Menü selbst steht er beim Anruf schon in `zusatz`.
     */
    datum?: string;
    /**
     * Erste Zeilen des Inhalts (PO 09.09.2026). Nur für die Mail-Karte; die
     * Auswahlliste bleibt einzeilig, sonst wäre sie keine Liste mehr.
     */
    anriss?: string;
};

export type BezugQuellen = {
    mails?: Array<{
        id: number;
        subject?: string | null;
        from?: string | null;
        /** Für die Karte im Notiztext, nicht für das Menü. */
        receivedAt?: string | null;
        preview?: string | null;
    }>;
    anrufe?: Array<{ id: number; displayName?: string | null; startedAt?: string | null }>;
    dokumente?: Array<{ id: number; fileName?: string | null }>;
    risiken?: Array<{ id: number; label?: string | null }>;
    vertraege?: Array<{ id: number; label?: string | null }>;
    personen?: Array<{ id: string; name?: string | null }>;
};

const datum = (iso?: string | null): string | undefined => {
    if (!iso) return undefined;
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? undefined
        : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
};

/** Zeitpunkt der Mail-Karte — mit Uhrzeit, weil an einem Tag mehrere kommen. */
const zeitpunkt = (iso?: string | null): string | undefined => {
    if (!iso) return undefined;
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? undefined
        : d.toLocaleString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
          });
};

/** Höchstlänge des Anrisses auf der Karte — darunter wird sie zum Mailfenster. */
const MAX_ANRISS = 160;

const anrissAus = (roh?: string | null): string | undefined => {
    const text = (roh ?? "").replace(/\s+/g, " ").trim();
    if (!text) return undefined;
    return text.length > MAX_ANRISS ? `${text.slice(0, MAX_ANRISS).trimEnd()} …` : text;
};

export function bezugKandidaten(q: BezugQuellen): BezugKandidat[] {
    const out: BezugKandidat[] = [];
    for (const m of q.mails ?? []) {
        out.push({
            art: "mail",
            id: m.id,
            titel: m.subject?.trim() || "(Kein Betreff)",
            ...(m.from ? { zusatz: m.from } : {}),
            ...(zeitpunkt(m.receivedAt) ? { datum: zeitpunkt(m.receivedAt) as string } : {}),
            ...(anrissAus(m.preview) ? { anriss: anrissAus(m.preview) as string } : {}),
        });
    }
    // Ein Anruf an mehreren To-dos steht seit dem 01.10.2026 mehrfach im
    // Verlauf — als Bezug genügt er einmal.
    const anrufeGesehen = new Set<number>();
    for (const c of q.anrufe ?? []) {
        if (anrufeGesehen.has(c.id)) continue;
        anrufeGesehen.add(c.id);
        out.push({
            art: "anruf",
            id: c.id,
            titel: c.displayName?.trim() || "Anruf",
            ...(datum(c.startedAt) ? { zusatz: datum(c.startedAt) as string } : {}),
        });
    }
    for (const d of q.dokumente ?? []) {
        out.push({ art: "dokument", id: d.id, titel: d.fileName?.trim() || "Dokument" });
    }
    for (const r of q.risiken ?? []) {
        out.push({ art: "risiko", id: r.id, titel: r.label?.trim() || `Risiko ${r.id}` });
    }
    for (const v of q.vertraege ?? []) {
        out.push({ art: "vertrag", id: v.id, titel: v.label?.trim() || `Vertrag ${v.id}` });
    }
    for (const p of q.personen ?? []) {
        out.push({ art: "person", id: p.id, titel: p.name?.trim() || String(p.id) });
    }
    return out;
}

/** Suche über Titel UND Zusatz — man sucht die Mail oft über den Absender. */
export function filterKandidaten(alle: BezugKandidat[], query: string): BezugKandidat[] {
    const q = query.trim().toLowerCase();
    if (!q) return alle;
    return alle.filter(
        (k) => k.titel.toLowerCase().includes(q) || (k.zusatz ?? "").toLowerCase().includes(q),
    );
}
